import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Col, Row} from 'react-bootstrap';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import StyleConfigPanel from '../data-lineage/components/StyleConfigPanel';
import CharacteristicsPanel from './components/CharacteristicsPanel';
import NodeRefsPanel from './components/NodeRefsPanel';
import SampleLineageCanvas from './components/SampleLineageCanvas';
import {mergeTheme} from '../data-lineage/util/theme';
import Auth from '../../auth/auth';
import * as A from '../data-lineage/store/lineageActions';
import './lineage-config.scss';

const auth = new Auth();

class LineageConfig extends Component {
  state = {
    activeTab: 0,
    draftTheme: null,
    dirty: false,
    saving: false,
    saveError: null,
  };

  componentDidMount() {
    this.props.loadGlobalSettings();
    this.props.loadCharacteristics(true);
    this.props.loadNodeRefs(true);
    if (this.props.globalTheme) {
      this.setState({draftTheme: mergeTheme(this.props.globalTheme)});
    }
  }

  componentDidUpdate(prev) {
    if (prev.globalTheme !== this.props.globalTheme && !this.state.dirty) {
      this.setState({draftTheme: mergeTheme(this.props.globalTheme)});
    }
  }

  onThemeChange = (next) => {
    this.setState({draftTheme: next, dirty: true});
  };

  onSave = () => {
    this.setState({saving: true, saveError: null});
    this.props.saveGlobalSettings(this.state.draftTheme)
        .then(() => this.setState({dirty: false, saving: false, saveError: null}))
        .catch((e) => this.setState({saving: false, saveError: e.message || 'Save failed'}));
  };

  onReset = () => {
    this.setState({draftTheme: mergeTheme(this.props.globalTheme), dirty: false});
  };

  render() {
    const {globalThemeLoading, characteristics} = this.props;
    const {activeTab, draftTheme, dirty, saving, saveError} = this.state;

    if (globalThemeLoading || draftTheme === null) {
      return (
        <div style={{padding: 24, display: 'flex', alignItems: 'center', gap: 12}}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading…</Typography>
        </div>
      );
    }

    const isSuperUser = auth.getIsSuperUser();
    const mergedTheme = mergeTheme(draftTheme);

    const saveBtn = (
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={this.onSave}
        disabled={!dirty || saving || !isSuperUser}
        data-testid="lc-save-btn"
      >
        {saving ? 'Saving…' : 'Save'}
      </Button>
    );

    return (
      <div className="lineage-config-page" data-testid="lineage-config-page">
        <div className="lc-header">
          <h3>Lineage Configuration</h3>
          {isSuperUser ? saveBtn : (
            <Tooltip title="Requires super user">{saveBtn}</Tooltip>
          )}
          <Button
            size="small"
            onClick={this.onReset}
            disabled={!dirty}
            data-testid="lc-reset-btn"
          >
            Reset
          </Button>
          {saveError && <span className="lc-save-error">{saveError}</span>}
        </div>

        <Row>
          <Col md={5}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => this.setState({activeTab: v})}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Style & Colour" data-testid="lc-tab-style" />
              <Tab label="Characteristics" data-testid="lc-tab-characteristics" />
              <Tab label="Node References" data-testid="lc-tab-noderefs" />
            </Tabs>

            <div style={{marginTop: 12}}>
              {activeTab === 0 && (
                <StyleConfigPanel
                  theme={mergedTheme}
                  onThemeChange={this.onThemeChange}
                />
              )}
              {activeTab === 1 && <CharacteristicsPanel />}
              {activeTab === 2 && <NodeRefsPanel />}
            </div>
          </Col>

          <Col md={7}>
            <div className="lc-canvas-wrap">
              <SampleLineageCanvas
                theme={mergedTheme}
                characteristics={characteristics}
              />
            </div>
          </Col>
        </Row>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  globalTheme: state.lineageReducer.globalTheme,
  globalThemeLoading: state.lineageReducer.globalThemeLoading,
  characteristics: state.lineageReducer.characteristics || [],
  nodeRefs: state.lineageReducer.nodeRefs || [],
});

const mapDispatchToProps = {
  loadGlobalSettings: A.loadGlobalSettings,
  saveGlobalSettings: A.saveGlobalSettings,
  loadCharacteristics: A.loadCharacteristics,
  loadNodeRefs: A.loadNodeRefs,
};

export default connect(mapStateToProps, mapDispatchToProps)(LineageConfig);
